import type { ReactNode } from 'react'

import { PrintAllCampuses } from '@/components/chefs/print/print-all-campuses'
import { PrintCoverPage } from '@/components/chefs/print/print-cover-page'
import { PrintDayCoverPage } from '@/components/chefs/print/print-day-cover-page'
import { PrintDeliverablesPage } from '@/components/chefs/print/print-deliverables-page'
import { PrintIngredientList } from '@/components/chefs/print/print-ingredient-list'
import { PrintPreliminaryScope } from '@/components/chefs/print/print-preliminary-watermark'
import { PrintRecipePack } from '@/components/chefs/print/print-recipe-pack'
import { PrintRouteSheet, PrintRouteUnavailable } from '@/components/chefs/print/print-route-sheet'
import {
  COVER_BLOCK_ID,
  deliverablesBlockId,
  INGREDIENTS_BLOCK_ID,
  orderedSheetsFor,
  ordersCoverBlockId,
  type PrintTarget,
  recipesCoverBlockId,
  ROUTE_UNAVAILABLE_BLOCK_ID,
  routeBlockId,
  routePrintDays,
  routeUnavailable,
  type TocEntry,
} from '@/lib/chefs/print-plan'

export type { PrintDay, PrintSections, PrintTarget } from '@/lib/chefs/print-plan'

interface PrintRootProps {
  target: PrintTarget
  /** Resolved table-of-contents entries (with measured page numbers). */
  toc: TocEntry[]
}

export function PrintRoot({ target, toc }: PrintRootProps) {
  const {
    sections,
    days,
    kitchenName,
    rangeDateLabel,
    uniqueCampusCount,
    uniqueRecipeCount,
    totalServings,
    showDayCovers,
    preliminary,
  } = target
  // Each entry is one top-level page-break block. `measureId` is set only on
  // leaf blocks whose height we measure directly; composite blocks
  // (PrintAllCampuses / PrintRecipePack) tag their own inner leaf pages.
  // `locksAtLabel` marks the block INCOMPLETE ORDER while a school it counts
  // can still change its order (PROD-1452).
  const blocks: { key: string; measureId?: string; locksAtLabel?: string; node: ReactNode }[] = []

  blocks.push({
    key: 'cover',
    measureId: COVER_BLOCK_ID,
    locksAtLabel: preliminary?.locksAtLabel,
    node: (
      <PrintCoverPage
        kitchenName={kitchenName}
        rangeDateLabel={rangeDateLabel}
        sections={sections}
        dayCount={days.length}
        uniqueCampusCount={uniqueCampusCount}
        uniqueRecipeCount={uniqueRecipeCount}
        totalServings={totalServings}
        toc={toc}
      />
    ),
  })

  if (sections.ingredients) {
    // ONE list for the whole print range: every picked day's campuses summed
    // (Abner, 164:858: "If the print range is greater than one day, then the
    // ingredient list should be for the whole period").
    const aggregateCampuses = days.flatMap((d) => d.campuses)
    blocks.push({
      key: 'ingredients',
      measureId: INGREDIENTS_BLOCK_ID,
      locksAtLabel: preliminary?.locksAtLabel,
      node: (
        <PrintIngredientList
          campuses={aggregateCampuses}
          ingredients={target.ingredients}
          kitchenName={kitchenName}
          dateLabel={rangeDateLabel}
          firstDay={days[0]!.date}
          lastDay={days[days.length - 1]!.date}
        />
      ),
    })
  }

  if (sections.deliverables) {
    for (const day of days) {
      if (day.campuses.length === 0) {
        continue
      }
      blocks.push({
        key: `deliverables-${day.iso}`,
        measureId: deliverablesBlockId(day.iso),
        locksAtLabel: preliminary?.dayLocksAtLabel[day.iso],
        node: (
          <PrintDeliverablesPage
            iso={day.iso}
            date={day.date}
            campuses={day.campuses}
            kitchenName={kitchenName}
            dateLabel={day.dateLabel}
          />
        ),
      })
    }
  }

  if (sections.summary) {
    for (const day of days) {
      if (day.campuses.length === 0) {
        continue
      }
      if (showDayCovers) {
        blocks.push({
          key: `orders-cover-${day.iso}`,
          measureId: ordersCoverBlockId(day.iso),
          locksAtLabel: preliminary?.dayLocksAtLabel[day.iso],
          node: <PrintDayCoverPage day={day} variant="orders" kitchenName={kitchenName} />,
        })
      }
      // No block-wide scope: each school's sheet is judged on its own windows.
      blocks.push({
        key: `orders-${day.iso}`,
        node: (
          <PrintAllCampuses
            iso={day.iso}
            date={day.date}
            sheets={orderedSheetsFor(target, day)}
            kitchenName={kitchenName}
            campusLocksAtLabel={preliminary?.campusLocksAtLabel}
          />
        ),
      })
    }
  }

  if (sections.recipes) {
    for (const day of days) {
      if (day.campuses.length === 0) {
        continue
      }
      if (showDayCovers) {
        blocks.push({
          key: `recipes-cover-${day.iso}`,
          measureId: recipesCoverBlockId(day.iso),
          locksAtLabel: preliminary?.dayLocksAtLabel[day.iso],
          node: <PrintDayCoverPage day={day} variant="recipes" kitchenName={kitchenName} />,
        })
      }
      blocks.push({
        key: `recipes-${day.iso}`,
        locksAtLabel: preliminary?.dayLocksAtLabel[day.iso],
        node: (
          <PrintRecipePack
            iso={day.iso}
            campuses={day.campuses}
            packagingCampuses={orderedSheetsFor(target, day).map((sheet) => sheet.campus)}
            kitchenName={kitchenName}
            dateLabel={day.dateLabel}
          />
        ),
      })
    }
  }

  // The driver's route sheet, last (buildPrintStructure lists it last too).
  if (sections.route) {
    if (routeUnavailable(target)) {
      // `days` always has one (PrintTarget's contract).
      blocks.push({
        key: 'route-unavailable',
        measureId: ROUTE_UNAVAILABLE_BLOCK_ID,
        node: (
          <PrintRouteUnavailable
            kitchenName={kitchenName}
            firstDay={days[0]!.date}
            lastDay={days[days.length - 1]!.date}
          />
        ),
      })
    }
    const route = target.route
    if (route) {
      for (const { day, route: routeDay } of routePrintDays(target)) {
        blocks.push({
          key: `route-${day.iso}`,
          measureId: routeBlockId(day.iso),
          locksAtLabel: preliminary?.dayLocksAtLabel[day.iso],
          node: (
            <PrintRouteSheet
              routeDay={{
                day: routeDay,
                date: day.date,
                campusesById: new Map(day.campuses.map((campus) => [campus.id, campus])),
              }}
              kitchen={route.kitchen}
              stopMinutes={route.settings.stopMinutes}
              driveStatus={route.driveTimes.status}
            />
          ),
        })
      }
    }
  }

  return (
    <>
      {blocks.map(({ key, measureId, locksAtLabel, node }, i) => (
        <div key={key} data-print-block={measureId} className={i < blocks.length - 1 ? 'print-page-break' : undefined}>
          <PrintPreliminaryScope locksAtLabel={locksAtLabel}>{node}</PrintPreliminaryScope>
        </div>
      ))}
    </>
  )
}
